import { Box, Button, Grid2, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const Step1 = ({ sra, setSra, setStep, storedUser }) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const orgId = JSON.parse(localStorage.getItem("org"));
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    const getVendors = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/vendor-list/org?orgId=${orgId}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        setVendors(res.data);
      } catch (error) {
        console.log(error.response?.data?.Exception || "Access denied");
      }
    };

    getVendors();
  }, []);

  const handleVendorChange = (e) => {
    const selectedCode = e.target.value;

    const selectedVendor = vendors.find(
      (vendor) => vendor.code === selectedCode,
    );

    if (selectedVendor) {
      setSra((prev) => ({
        ...prev,
        code: selectedVendor.code || "",
        name: selectedVendor.name || "",
      }));
    } else {
      setSra((prev) => ({
        ...prev,
        code: "",
        name: "",
      }));
    }

    setErrors((prev) => ({
      ...prev,
      code: "",
      name: "",
    }));
  };

  const handleChange = (e) => {
    setSra({ ...sra, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    const data = sra || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    if (isEmpty(data.title)) {
      newErrors.title = "Title is required";
    }

    if (isEmpty(data.name)) {
      newErrors.name = "Vendor Name/ID is required";
    }

    if (isEmpty(data.code)) {
      newErrors.code = "Vendor Code is required";
    }

    if (isEmpty(data.proformaId)) {
      newErrors.proformaId = "Proforma ID is required";
    }

    if (!data.date) {
      newErrors.date = "Date of Proforma is required";
    }

    if (isEmpty(data.advAmount)) {
      newErrors.advAmount = "Advance Amount is required";
    } else if (isNaN(data.advAmount) || Number(data.advAmount) < 0) {
      newErrors.advAmount = "Enter a valid amount";
    }

    if (isEmpty(data.history)) {
      newErrors.history = "Vendor History is required";
    } else if (data.history.length > 250) {
      newErrors.history = "Vendor History must be up to 250 characters";
    }

    if (isEmpty(data.reference)) {
      newErrors.reference = "Reference is required";
    } else if (data.reference.length > 250) {
      newErrors.reference = "Reference must be up to 250 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/draft-vendor-advance`,
        {
          username: storedUser.username,
          title: sra.title,
          sapNumber: sra.sapNumber,
          name: sra.name,
          code: sra.code,
          proformaId: sra.proformaId,
          date: sra.date,
          advAmount: sra.advAmount,
          details: sra.details,
          history: sra.history,
          reference: sra.reference,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setSra((prev) => ({
        ...prev,
        ...res.data,
        justification: res.data.justification || prev.justification || "",
        paymentTerms: res.data.paymentTerms || prev.paymentTerms || "",
        attachment1: res.data.attachment1 || prev.attachment1 || "",
        attachment2: res.data.attachment2 || prev.attachment2 || "",
        attachment3: res.data.attachment3 || prev.attachment3 || "",
      }));
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/draft-vendor-advance?draftSraId=${sra.draftVendorAdvanceId}`,
        {
          title: sra.title,
          sapNumber: sra.sapNumber,
          name: sra.name,
          code: sra.code,
          proformaId: sra.proformaId,
          date: sra.date,
          advAmount: sra.advAmount,
          details: sra.details,
          history: sra.history,
          reference: sra.reference,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/approval/dashboard");
  };

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <h2>Vendor Advance</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "20%" }}></span>
      </div>
      <div className="dashboard-container">
        <Typography className="form-title">Details</Typography>

        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="title">
              Title <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.title && "input-error"}`}
              type="text"
              name="title"
              id="title"
              onChange={handleChange}
              value={sra?.title || ""}
            />
            {errors.title && <p className="error">{errors.title}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="sapNumber">SAP Document Number</label>
            <input
              className={`${errors.sapNumber && "input-error"}`}
              type="text"
              name="sapNumber"
              id="sapNumber"
              onChange={handleChange}
              value={sra?.sapNumber || ""}
            />
            {errors.sapNumber && <p className="error">{errors.sapNumber}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="code">
              Vendor Code <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <select
              name="code"
              id="code"
              onChange={handleVendorChange}
              value={sra?.code || ""}
              className={`${errors.code && "input-error"}`}
            >
              <option value="">---Select---</option>
              {vendors?.map((vendor) => (
                <option key={vendor.code} value={vendor.code}>
                  {vendor.code}
                </option>
              ))}
            </select>
            {errors.code && <p className="error">{errors.code}</p>}
          </Grid2>
          
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="name">
              Vendor Name/ID <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.name && "input-error"}`}
              type="text"
              name="name"
              id="name"
              onChange={handleChange}
              value={sra?.name || ""}
            />
            {errors.name && <p className="error">{errors.name}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="proformaId">
              Proforma ID <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.proformaId && "input-error"}`}
              type="text"
              name="proformaId"
              id="proformaId"
              onChange={handleChange}
              value={sra?.proformaId || ""}
            />
            {errors.proformaId && <p className="error">{errors.proformaId}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="date">
              Date of Proforma <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.date && "input-error"}`}
              type="date"
              name="date"
              id="date"
              onChange={handleChange}
              value={
                sra?.date ? new Date(sra.date).toISOString().split("T")[0] : ""
              }
            />
            {errors.date && <p className="error">{errors.date}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="advAmount">
              Advance Amount <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.advAmount && "input-error"}`}
              type="number"
              step="0.01"
              name="advAmount"
              id="advAmount"
              onChange={handleChange}
              value={sra?.advAmount || ""}
            />
            {errors.advAmount && <p className="error">{errors.advAmount}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="details">Bank Details</label>
            <textarea
              className={`${errors.details && "input-error"}`}
              name="details"
              id="details"
              onChange={handleChange}
              value={sra?.details || ""}
              rows={4}
            />
            {errors.details && <p className="error">{errors.details}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="history">
              Vendor History <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <textarea
              className={`${errors.history && "input-error"}`}
              name="history"
              id="history"
              onChange={handleChange}
              value={sra?.history || ""}
              rows={4}
            />
            {errors.history && <p className="error">{errors.history}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="reference">
              Reference <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <textarea
              className={`${errors.reference && "input-error"}`}
              name="reference"
              id="reference"
              onChange={handleChange}
              value={sra?.reference || ""}
              rows={4}
            />
            {errors.reference && <p className="error">{errors.reference}</p>}
          </Grid2>
        </Grid2>
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={sra && sra.draftVendorAdvanceId ? handleUpdate : handleNext}
          disabled={btnLoading}
        >
          {btnLoading ? "Submitting..." : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step1;
